import { Box, Grid } from "@mui/material";
import dayjs from "dayjs";
import { FormikHandlers } from "formik";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import AsyncSelect from "../../../../../../../components/AsyncSelect";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../../components/CustomSelect";
import Input from "../../../../../../../components/Input";
import MaskMultiSelect from "../../../../../../../components/MaskMultiSelect";
import { ScrollArea } from "../../../../../../../components/ScrollBar";
import Text from "../../../../../../../components/Text";
import ProviderSelect from "../../../../../../../components/ProviderSelect";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import useGetCataLogCode from "../../../../../../../queries/catalog/use-get-catalog-code";
import { useGetPharmacyByType } from "../../../../../../../queries/patient-chart/order/use-get-pharmacy-by-type";
import { DEF_PAGINATION, getXtenantId } from "../../../../../../../utils/utils";
import {
  PrescriptionForm,
  SUBSTITUTIONS_OR_DISPENSE_OPTIONS_LABELS,
  TYPE_OPTIONS,
} from "../../../../patient/patient-list/patient-chart/order/order-utils";
// import { NewMedicationDrawer } from "./NewPublicMedAddDrawer";
import NumberInput from "../../../../../../../components/NumberInput";
import DrugCatalogDrawer from "../../../../../../../components/app/admin/Library/catalog/DrugCatalogDrawer";
import { useMultiSelectDataController } from "../../../../../../../hooks/useMultiSelectDataController";
import { INPUT_FIELD_HEIGHT } from "../../../../../../../theme";

interface PrescriptionFieldsProps {
  values: PrescriptionForm;
  readOnly?: boolean;
  touched?: any;
  errors?: any;
  handleBlur: FormikHandlers["handleBlur"];
  handleChange: FormikHandlers["handleChange"];
  setFieldValue: any;
  icdCodes?: { value: string; label: string }[];
}

export const PrescriptionFields = ({
  values,
  handleBlur,
  touched,
  errors,
  handleChange,
  setFieldValue,
  readOnly,
  icdCodes,
}: PrescriptionFieldsProps) => {
  const { patientId } = useParams();
  const [pharmacyKeys, setPharmacyKeys] = useDebounce<string>("");
  const newMedDisc = useDisclosure();

  const [icdSearch, setIcdSearch] = useDebounce<string>("");
  const { data: icdCodesRaw } = useGetCataLogCode({
    pagination: DEF_PAGINATION,
    searchKey: icdSearch,
    type: "ICD",
    active: undefined,
    archive: false,
  });

  const { data: allPharmacies } = useGetPharmacyByType({
    pagination: DEF_PAGINATION,
    patientUuid: patientId ?? "",
    type: "PHARMACY",
    searchString: pharmacyKeys,
    xTENANTID: getXtenantId(),
  });

  const [medicationSearch, setMedicationSearch] = useDebounce<string>("");

  const { data: medicinesList } = useGetCataLogCode({
    gridSorting: [],
    pagination: DEF_PAGINATION,
    searchKey: medicationSearch,
    type: "DRUG_CATALOG",
    archive: false,
  });

  const allIcdCodes = useMemo(
    () =>
      icdCodesRaw?.data?.content?.map((l) => ({
        value: l?.uuid,
        label: `${l?.code} - ${l?.description || ""}`,
      })) ?? [],
    [icdCodesRaw, icdCodes]
  );
  const icdCodesController = useMultiSelectDataController(
    allIcdCodes,
    icdCodes
  );

  const allPharmaciesOptions = useMemo(() => {
    return allPharmacies?.data?.content?.map((it) => ({
      value: it?.uuid,
      label: it?.name,
    }));
  }, [allPharmacies?.data?.content]);

  const transformedMedicines = useMemo(() => {
    return (
      medicinesList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.drugName + " - " + (it?.description ?? ""),
      })) ?? []
    );
  }, [medicinesList]);

  return (
    <ScrollArea height={"calc(100vh - 170px)"} p={1}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        {/* <NewMedicationDrawer disclosureHost={newMedDisc} /> */}
        <DrugCatalogDrawer
          dialogTitle={"Add Medication"}
          disclosureHost={newMedDisc}
        />

        <Grid container spacing={2}>
          <Grid size={4}>
            <CustomSelect
              required
              readOnly={readOnly}
              label="Type"
              placeholder="Type"
              options={TYPE_OPTIONS}
              name="type"
              value={values.prescriptionFormType}
              onChange={(v) => setFieldValue("prescriptionFormType", v)}
              error={
                touched?.prescriptionFormType && errors?.prescriptionFormType
              }
            />
          </Grid>
          <Grid container spacing={2} size={8}>
            <Grid size={7.5}>
              <AsyncSelect
                label="Medication Name"
                required
                disabled={readOnly}
                placeholder="Select Or Search Medicine"
                name="medicationId"
                handleBlur={handleBlur}
                onSearch={setMedicationSearch}
                options={transformedMedicines}
                value={values?.medicationId}
                error={touched?.medicationId && errors?.medicationId}
                onChange={(v) => setFieldValue("medicationId", v)}
              />
            </Grid>
            <Grid size={4.5}>
              {!readOnly && (
                <StyledButtonV2
                  fullWidth
                  sx={{ height: INPUT_FIELD_HEIGHT }}
                  shouldAddIcon
                  onClick={() => newMedDisc.open()}
                  variant="outlined"
                  label="New Medication"
                />
              )}
            </Grid>
          </Grid>
          <Grid size={4}>
            <ProviderSelect
              disabled={readOnly}
              required
              label="Provider (Sign As)"
              name="provider"
              handleBlur={handleBlur}
              value={values?.provider ?? null}
              onChange={(v) => setFieldValue("provider", v)}
              prefillListOption={values?.provider ?? undefined}
              error={touched?.provider && errors?.provider}
            />
          </Grid>
          {/* <Grid item xs={4}>
            <CustomSelect
              readOnly={readOnly}
              label="Facility"
              options={FACILITY_OPTIONS}
              name="type"
              value={values?.facility}
              onChange={(v) => setFieldValue("facility", v)}
            />
          </Grid> */}
          <Grid size={4}>
            <CustomDatePicker
              required
              disabled={readOnly}
              maxDate={dayjs(new Date())}
              label="Prescription Date"
              placeholder="Choose Date"
              name="prescriptionDate"
              handleBlur={handleBlur}
              value={
                values.prescriptionDate
                  ? dayjs(values.prescriptionDate)
                  : undefined
              }
              onChange={(date) =>
                setFieldValue("prescriptionDate", date?.toISOString())
              }
              error={errors?.prescriptionDate as string | undefined}
            />
          </Grid>
          <Grid
            sx={{
              mt: 1,
            }}
            size={12}
          >
            <Text>INSTRUCTIONS</Text>
          </Grid>
          <Grid size={12}>
            <Input
              label="Directions to Patient"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter",
                name: "directionToPatient",
                value: values.directionToPatient ?? "",
                onChange: handleChange,
              }}
            />
          </Grid>
          <Grid size={4}>
            <NumberInput
              min={1}
              label="Duration"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter",
                name: "duration",
                value: values.duration ?? "",
                onChange: handleChange,
              }}
              error={touched?.duration && errors?.duration}
            />
          </Grid>
          <Grid size={4}>
            <NumberInput
              min={1}
              label="Quantity"
              required
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter",
                name: "quantity",
                value: values.quantity ?? "",
                onChange: handleChange,
              }}
              error={touched?.quantity && errors?.quantity}
            />
          </Grid>
          <Grid size={4}>
            <CustomSelect
              readOnly={readOnly}
              required={!!values?.quantity}
              label="Unit"
              name="unit"
              handleBlur={handleBlur}
              placeholder="Select Unit"
              options={UNIT_OPTIONS}
              value={values?.unit}
              onChange={(v) => setFieldValue("unit", v)}
              error={touched?.unit && errors?.unit}
            />
          </Grid>
          <Grid size={4}>
            <NumberInput
              label="Refill"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Refill",
                name: "refills",
                value: values.refills ?? "",
                onChange: handleChange,
              }}
              error={touched?.refills && errors?.refills}
            />
          </Grid>
          <Grid size={4}>
            <CustomDatePicker
              disabled={readOnly}
              required={!!values?.duration}
              label="Start Date"
              name="startDate"
              handleBlur={handleBlur}
              placeholder="Choose Date"
              value={values.startDate ? dayjs(values.startDate) : undefined}
              onChange={(date) =>
                setFieldValue("startDate", date?.toISOString())
              }
              error={touched?.startDate && errors?.startDate}
            />
          </Grid>
          <Grid size={4}>
            <CustomDatePicker
              disabled={readOnly}
              required={!!values?.duration}
              minDate={dayjs(new Date())}
              label="End Date"
              name="endDate"
              handleBlur={handleBlur}
              placeholder="Choose Date"
              value={values.endDate ? dayjs(values.endDate) : undefined}
              onChange={(date) => setFieldValue("endDate", date?.toISOString())}
              error={errors?.endDate}
            />
          </Grid>{" "}
          <Grid size={4}>
            <CustomSelect
              readOnly={readOnly}
              required
              label="Substitutions or Dispense"
              name="substitutionsOrDispense"
              handleBlur={handleBlur}
              placeholder="Select Substitutions or Dispense"
              options={SUBSTITUTIONS_OR_DISPENSE_OPTIONS_LABELS}
              value={values?.substitutionsOrDispense}
              onChange={(v) => setFieldValue("substitutionsOrDispense", v)}
              error={
                touched?.substitutionsOrDispense &&
                errors?.substitutionsOrDispense
              }
            />
          </Grid>
          <Grid
            sx={{
              mt: 1,
            }}
            size={12}
          >
            <Text>PHARMACY</Text>
          </Grid>
          <Grid size={12}>
            <AsyncSelect
              disabled={readOnly}
              handleBlur={handleBlur}
              name="preferences"
              required
              label="Pharmacy"
              placeholder="Search and Select Pharmacy"
              options={allPharmaciesOptions}
              value={values?.preferences}
              onSearch={setPharmacyKeys}
              onChange={(v) => setFieldValue("preferences", v)}
              error={touched?.preferences && errors?.preferences}
            />
          </Grid>
          <Grid size={4}>
            <CustomDatePicker
              disabled={readOnly}
              label="Do not fill before"
              placeholder="Select Date"
              value={
                values.notFillBefore ? dayjs(values.notFillBefore) : undefined
              }
              onChange={(val) =>
                setFieldValue("notFillBefore", val?.toISOString())
              }
              error={errors?.notFillBefore}
            />
          </Grid>
          <Grid size={4}>
            <CustomSelect
              readOnly={readOnly}
              label="Preferred Delivery"
              placeholder="Select Preferred Delivery Method"
              options={[{ value: "PRINT", label: "Print" }]}
              value={values.preferredDelivery}
              onChange={(v) => setFieldValue("preferredDelivery", v)}
            />
          </Grid>
          <Grid size={4}>
            <Input
              label="Prior Authorization"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter",
                name: "priorAuthorization",
                value: values.priorAuthorization ?? "",
                onChange: handleChange,
              }}
            />
          </Grid>
          <Grid size={12}>
            <Input
              label="Directions to Pharmacist"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter Directions for Pharmacist",
                name: "directionToPharmacist",
                value: values.directionToPharmacist ?? "",
                onChange: handleChange,
              }}
            />
          </Grid>
          <Grid
            sx={{
              mt: 1,
            }}
            size={12}
          >
            <Text>DIAGNOSES</Text>
          </Grid>
          <Grid size={12}>
            <MaskMultiSelect
              handleBlur={handleBlur}
              name="medicalCodes"
              required
              disabled={readOnly}
              label="Diagnosis (ICD-10)"
              placeholder="Search and Select Code"
              options={icdCodesController}
              onSearch={setIcdSearch}
              value={values.medicalCodes}
              onChange={(v) => setFieldValue("medicalCodes", v)}
              error={touched?.medicalCodes && errors?.medicalCodes}
            />
          </Grid>
        </Grid>
      </Box>
    </ScrollArea>
  );
};

export const UNIT_OPTIONS = [
  { label: "Percent", value: "PERCENT" },
  { label: "Capsule", value: "CAPSULE" },
  { label: "Drops", value: "DROPS" },
  { label: "g", value: "G" },
  { label: "Iu", value: "IU" },
  { label: "Mcg", value: "MCG" },
  { label: "Meq", value: "MEQ" },
  { label: "Mg", value: "MG" },
  { label: "MgPerKg", value: "MG_PER_KG" },
  { label: "MgPerMl", value: "MG_PER_ML" },
  { label: "Ml", value: "ML" },
  { label: "MlPerHour", value: "ML_PER_HOUR" },
  { label: "Puffs", value: "PUFFS" },
  { label: "Sprays", value: "SPRAYS" },
  { label: "Tablespoon", value: "TABLESPOON" },
  { label: "Tablets", value: "TABLETS" },
  { label: "Teaspoon", value: "TEASPOON" },
  { label: "Unit", value: "UNIT" },
];
